import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Calculator, ExternalLink, RefreshCw, Scale, AlertTriangle, ShieldAlert, Info, Download, MapPin, Archive } from "lucide-react";
import { toast } from "sonner";

const TERR = [
  { k: "I", label: "I — mare/lago aperto" },
  { k: "II", label: "II — campagna, ostacoli radi" },
  { k: "III", label: "III — periferia/campagna" },
  { k: "IV", label: "IV — aree urbane" },
  { k: "V", label: "V — centri urbani/boschi" },
];
const FRICTION = [
  { k: "guaina_bituminosa", label: "Guaina bituminosa" },
  { k: "pvc", label: "PVC" },
  { k: "tpo", label: "TPO/FPO" },
  { k: "epdm", label: "EPDM" },
  { k: "calcestruzzo", label: "Calcestruzzo" },
  { k: "tappetino_su_guaina", label: "Tappetino protettivo su guaina" },
  { k: "tappetino_su_pvc", label: "Tappetino protettivo su PVC" },
  { k: "tappetino_su_tpo", label: "Tappetino protettivo su TPO" },
  { k: "tappetino_su_epdm", label: "Tappetino protettivo su EPDM" },
  { k: "sconosciuta", label: "Superficie sconosciuta / altro" },
];

// scala colore per peso zavorra (verde -> rosso)
const weightColor = (n, maxN) => {
  if (!maxN) return "#22c55e";
  const t = Math.min(1, (n - 1) / Math.max(1, maxN - 1));
  const hue = 130 - t * 130; // 130=verde -> 0=rosso
  return `hsl(${hue}, 70%, 45%)`;
};

// layout presente se ci sono conteggi 'rows' oppure una 'matrice' reale dal configuratore
const faldaHasLayout = (f) => {
  const m = f?.matrice ?? f?.matrix;
  if (Array.isArray(m) && m.some((r) => Array.isArray(r) && r.some(Boolean))) return true;
  return Array.isArray(f?.rows) && f.rows.length > 0;
};

// numero originale della falda: preserva l'indice anche quando se ne calcolano solo alcune
const faldaNum = (f, i) => Number(f?.falda_n) || (i + 1);

// conteggio moduli di una falda (da matrice reale o da conteggi rows)
const faldaPanelCount = (f) => {
  const m = f?.matrice ?? f?.matrix;
  if (Array.isArray(m)) return m.reduce((a, r) => a + (Array.isArray(r) ? r.filter(Boolean).length : 0), 0);
  if (Array.isArray(f?.rows)) return f.rows.reduce((a, x) => a + (parseInt(x, 10) > 0 ? parseInt(x, 10) : 0), 0);
  return 0;
};

const STATE_STYLE = {
  "CALCOLO COMPLETO": "bg-emerald-600 text-white",
  "PRE-DIMENSIONAMENTO": "bg-amber-500 text-black",
  "DATI INSUFFICIENTI": "bg-slate-500 text-white",
  "NON VERIFICATO": "bg-red-600 text-white",
};

const cfgMatch = (c, q) => {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [c.installer?.ragione_sociale, c.agent?.name, c.summary?.comune, c.summary?.cliente].some((v) => (v || "").toLowerCase().includes(s));
};
const AdminCfgLine = ({ c }) => (
  <span className="block text-[11px] text-primary" data-testid={`admin-cfg-owner-${c.id}`}>
    {c.installer?.ragione_sociale || "—"} · {c.installer_type || (c.is_partner ? "partner" : "cliente")}{c.agent?.name ? ` · agente ${c.agent.name}` : ""} · {new Date(c.created_at).toLocaleDateString("it-IT")}
  </span>
);

export default function ZavorreDialog({ open, onOpenChange, installerId, installerName, installerIsPartner, preload, preloadKey, adminAll, link }) {
  const [configs, setConfigs] = useState([]);
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [selected, setSelected] = useState(null);
  const [faldeSel, setFaldeSel] = useState(null); // Set dei numeri falda selezionati (null = tutte)
  const [cfgQ, setCfgQ] = useState("");
  const instId = adminAll ? selected?.installer?.id : installerId;
  const instName = adminAll ? selected?.installer?.ragione_sociale : installerName;
  const instPartner = adminAll ? !!selected?.is_partner : installerIsPartner;
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [autoMode, setAutoMode] = useState(true);
  const [moduleAutoInfo, setModuleAutoInfo] = useState("");
  const [province, setProvince] = useState([]);
  const [provincia, setProvincia] = useState("");
  const [categoria, setCategoria] = useState("III");
  const [catInfo, setCatInfo] = useState("");
  const [friction, setFriction] = useState("guaina_bituminosa");
  const [frictionOpts, setFrictionOpts] = useState(FRICTION);
  const [contatto, setContatto] = useState("zavorra");
  const [sistema, setSistema] = useState("prsg");
  const [quota, setQuota] = useState("");
  const [altEd, setAltEd] = useState("");
  const [dSup, setDSup] = useState("");
  const [dInf, setDInf] = useState("");
  const [dSx, setDSx] = useState("");
  const [dDx, setDDx] = useState("");
  const [campoAperto, setCampoAperto] = useState(false);
  const [comune, setComune] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);
  const [activePrsg, setActivePrsg] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [savingCt, setSavingCt] = useState(false);
  const [usaZavorra, setUsaZavorra] = useState(false);
  const [pyDir, setPyDir] = useState("PY_UPLIFT");
  const [engineMode, setEngineMode] = useState("AERODYNAMIC_PRSG");
  const autoGeoRef = useRef(null);
  // campi REALMENTE acquisiti in automatico (bloccati solo questi quando autoMode è attivo)
  const [autoFilled, setAutoFilled] = useState({});
  const [hasParapet, setHasParapet] = useState(false);
  const [parapetH, setParapetH] = useState("");
  const [parapetDiff, setParapetDiff] = useState(false);
  const [pSup, setPSup] = useState("");
  const [pInf, setPInf] = useState("");
  const [pSx, setPSx] = useState("");
  const [pDx, setPDx] = useState("");

  const loadConfigs = useCallback(() => {
    if (link) { setConfigs([link.config]); setSelected(link.config); return; }
    if (!installerId && !adminAll) return;
    setLoadingCfg(true);
    api.get(adminAll ? "/rete/configs" : `/rete/configs?installer_id=${installerId}`)
      .then((r) => setConfigs((r.data?.configs || []).filter((c) => {
        const f = c.summary?.disposizione_falde || c.disposizione_falde || [];
        const hasLayout = Array.isArray(f) && f.some(faldaHasLayout);
        const rt = c.summary?.roof_type || c.roof_type;
        return hasLayout && c.tool === "tetto" && (rt === "piano" || rt === "inclinato");
      })))
      .catch(() => setConfigs([]))
      .finally(() => setLoadingCfg(false));
  }, [installerId, adminAll, link]);

  useEffect(() => {
    if (!open) return;
    setResult(null); setActivePrsg(null); setSelected(null);
    loadConfigs();
    api.get(link ? "/strumenti/catalogo?tipologia=Moduli%20Fotovoltaici" : "/listino?tipologia=Moduli%20Fotovoltaici&abilitato=1").then((r) => setModules(r.data?.products || [])).catch(() => setModules([]));
    api.get("/prsg-zavorre/config").then((r) => {
      const bs = r.data?.config?.torri?.friction_two_interface_by_surface || {};
      const opts = Object.entries(bs).filter(([, e]) => e?.show_in_form !== false).map(([k, e]) => ({ k, label: e.label || k.replace(/_/g, " ") }));
      if (opts.length) {
        setFrictionOpts(opts);
        setFriction((cur) => opts.some((o) => o.k === cur) ? cur : opts[0].k);
      }
    }).catch(() => {});
    api.get("/prsg-zavorre/province").then((r) => setProvince(r.data?.province || [])).catch(() => setProvince([]));
  }, [open, loadConfigs]);

  const falde = selected?.summary?.disposizione_falde || selected?.disposizione_falde || [];
  const hasFalde = falde.some(faldaHasLayout);
  // falde con layout + numero originale preservato
  const faldeLayout = useMemo(() => falde.map((f, i) => ({ f, n: faldaNum(f, i) })).filter((x) => faldaHasLayout(x.f)), [falde]);
  const selFaldeCount = faldeLayout.filter((x) => !faldeSel || faldeSel.has(x.n)).length;
  const allFaldeSel = faldeLayout.length > 0 && selFaldeCount === faldeLayout.length;
  const toggleFalda = (n) => {
    setResult(null);
    setFaldeSel((prev) => {
      const base = prev ? new Set(prev) : new Set(faldeLayout.map((x) => x.n));
      if (base.has(n)) base.delete(n); else base.add(n);
      return base;
    });
  };
  // inizializza/aggiorna la selezione falde al cambio consulenza (default: tutte)
  useEffect(() => {
    setFaldeSel(new Set(faldeLayout.map((x) => x.n)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);
  const roofType = selected?.summary?.roof_type || selected?.roof_type || null;
  const isInclinato = roofType === "inclinato";
  // tetto inclinato → calcolo SEMPRE in modalità complanare (con avviso), usando le metriche già in rete
  useEffect(() => { if (isInclinato) { setSistema("complanare"); setContatto("zavorra"); setResult(null); } }, [isInclinato]);
  const provInfo = useMemo(() => province.find((p) => p.sigla === provincia), [province, provincia]);
  // PRSG acquisita AUTOMATICAMENTE dall'inclinazione staffa della consulenza (non modificabile a mano)
  const staffaGradi = selected?.summary?.staffa_gradi;
  const gradiAuto = (() => { const t = staffaGradi != null ? parseFloat(staffaGradi) : NaN; return !Number.isNaN(t) ? (Math.abs(t - 10) <= Math.abs(t - 25) ? "10" : "25") : null; })();
  // sfondo campo: immagine satellitare del configuratore quando disponibile, altrimenti schema tetto
  const fieldImg = selected?.summary?.overlay_image_url || selected?.summary?.satellite_url || selected?.summary?.screenshot_url
    || selected?.summary?.immagine_url || selected?.summary?.map_url || selected?.summary?.snapshot_url || null;
  // schema pesi zavorre: griglia SCHEMATICA (mai sovrapposta alla vista satellitare del configuratore, che è un documento separato)
  const roofStyle = { backgroundImage: "repeating-linear-gradient(45deg, rgba(148,163,184,0.10) 0 10px, rgba(148,163,184,0.04) 10px 20px)", backgroundColor: "rgba(100,116,139,0.10)" };

  useEffect(() => {
    if (!selected || preload) return;
    setAutoMode(true);
    const s = selected.summary || {};
    const cm = s.comune || s.citta || s.city || "";
    const ind = s.indirizzo || s.address || s.via || "";
    let pv = String(s.provincia || s.prov || s.sigla_provincia || "").toUpperCase();
    if (!(pv && pv.length === 2)) {
      const m = String(ind).match(/\(([A-Za-z]{2})\)/) || String(s.installatore_sede || "").match(/\(([A-Za-z]{2})\)/);
      if (m) pv = m[1].toUpperCase();
    }
    const af = {};
    if (cm) { setComune(cm); af.comune = true; }
    if (ind) { setIndirizzo(ind); af.indirizzo = true; }
    if (pv && pv.length === 2) { setProvincia(pv); af.provincia = true; }
    const b = s.building || {};
    if (b.height_m || s.altezza_edificio) { setAltEd(String(b.height_m || s.altezza_edificio)); af.altEd = true; }
    setAutoFilled(af);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // acquisizione automatica quota/edificio dalla località della consulenza (una sola volta per consulenza)
  useEffect(() => {
    if (!selected || preload) return;
    const s = selected.summary || {};
    const cm = s.comune || "";
    const ind = s.indirizzo || "";
    let pv = ""; const mm = String(ind).match(/\(([A-Za-z]{2})\)/); if (mm) pv = mm[1].toUpperCase();
    if (!cm && !ind && !pv) return;
    if (autoGeoRef.current === selected.id) return;
    autoGeoRef.current = selected.id;
    setGeoLoading(true);
    api.get("/prsg-zavorre/geo", { params: { comune: cm, provincia: pv, indirizzo: ind } })
      .then((r) => {
        const d = r.data || {};
        const af = {};
        if (d.quota_m != null) { setQuota(String(d.quota_m)); af.quota = true; }
        if (d.terrain_category) {
          setCategoria(d.terrain_category);
          setCatInfo(`stimata da analisi OSM${d.terrain_reason ? ` — ${d.terrain_reason}` : ""} · modificabile`);
        }
        if (d.building) {
          if (d.building.height_m != null) { setAltEd(String(d.building.height_m)); af.altEd = true; }
        }
        setAutoFilled((prev) => ({ ...prev, ...af }));
      })
      .catch(() => {})
      .finally(() => setGeoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // acquisizione automatica del pannello: il configuratore non lo invia → inferito dal wattaggio o memorizzato
  useEffect(() => {
    if (!selected || !modules.length || preload) return;
    const s = selected.summary || {};
    const wattOf = (m) => {
      if (m.kwp) return Number(m.kwp) * 1000;
      const mm = String(m.descrizione || "").match(/(\d{3,4})\s*W/i);
      return mm ? Number(mm[1]) : 0;
    };
    const lock = (id, info) => { setModuleId(id); setModuleAutoInfo(info); };
    const mc = s.modulo || s.module_code || s.pannello || s.modulo_codice;
    if (mc) { const m = modules.find((x) => x.codice === mc); if (m) { lock(m.id, `acquisito dalla consulenza · ${m.codice}`); return; } }
    const rid = localStorage.getItem(`zav_mod_${selected.id}`);
    if (rid && modules.some((x) => x.id === rid)) { const m = modules.find((x) => x.id === rid); lock(rid, `già acquisito in precedenza · ${m.codice}`); return; }
    const np = Number(s.n_pannelli) || 0, pk = Number(s.potenza_kwp) || 0;
    if (np > 0 && pk > 0) {
      const wp = (pk * 1000) / np;
      const cand = modules.map((m) => ({ m, w: wattOf(m) })).filter((x) => x.w > 0)
        .map((x) => ({ ...x, d: Math.abs(x.w - wp) })).sort((a, b) => a.d - b.d);
      if (cand.length && cand[0].d <= 10) { lock(cand[0].m.id, `acquisito dalla consulenza · ~${Math.round(wp)} W → ${cand[0].m.codice}`); return; }
    }
    setModuleAutoInfo("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, modules]);

  // RIAPRI CONFIGURAZIONE: ricarica gli input da una consulenza tecnica salvata
  useEffect(() => {
    if (!open || !preload) return;
    const p = preload;
    const syn = { id: `ct-${preloadKey || "x"}`, created_at: null, consulenza_url: p.consulenza_url || p.meta?.consulenza_url || null,
      summary: { disposizione_falde: p.disposizione_falde || [], cliente: p.meta?.cliente || p.cliente || null,
        comune: p.meta?.comune || null, indirizzo: p.meta?.indirizzo || null, provincia: p.provincia || null,
        potenza_kwp: p.meta?.potenza_kwp ?? null } };
    autoGeoRef.current = syn.id;   // blocca il refetch geo che sovrascriverebbe quota/altezza
    setSelected(syn);
    setAutoMode(false);
    setProvincia(p.provincia || "");
    setQuota(p.quota_m != null ? String(p.quota_m) : "");
    setAltEd(p.altezza_edificio_m != null ? String(p.altezza_edificio_m) : "");
    setCategoria(p.categoria_terreno || "III");
    setFriction(p.friction_preset || "guaina_bituminosa");
    setContatto(p.contatto_tetto || "zavorra");
    setUsaZavorra(!!p.usa_zavorra);
    setModuleId(p.module_listino_id || "");
    const b = p.building || {};
    setCampoAperto(!!b.campo_aperto);
    setDSup(b.dist_sup_m ? String(b.dist_sup_m) : ""); setDInf(b.dist_inf_m ? String(b.dist_inf_m) : "");
    setDSx(b.dist_sx_m ? String(b.dist_sx_m) : ""); setDDx(b.dist_dx_m ? String(b.dist_dx_m) : "");
    const pp = p.parapetto || {};
    setHasParapet(!!pp.presente); setParapetDiff(!!pp.diverso_per_lato);
    setParapetH(pp.altezza_m ? String(pp.altezza_m) : "");
    setPSup(pp.h_sup_m ? String(pp.h_sup_m) : ""); setPInf(pp.h_inf_m ? String(pp.h_inf_m) : "");
    setPSx(pp.h_sx_m ? String(pp.h_sx_m) : ""); setPDx(pp.h_dx_m ? String(pp.h_dx_m) : "");
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preload, preloadKey]);

  const rileva = async () => {
    if (!comune && !provincia && !indirizzo) { toast.error("Inserisci almeno Comune o Provincia"); return; }
    setGeoLoading(true);
    try {
      const r = await api.get("/prsg-zavorre/geo", { params: { comune, provincia, indirizzo } });
      const d = r.data || {};
      const af = {};
      if (d.quota_m != null) { setQuota(String(d.quota_m)); af.quota = true; }
      if (d.terrain_category) {
        setCategoria(d.terrain_category);
        setCatInfo(`stimata da analisi OSM${d.terrain_reason ? ` — ${d.terrain_reason}` : ""} · modificabile`);
      }
      if (d.building) {
        if (d.building.height_m != null) { setAltEd(String(d.building.height_m)); af.altEd = true; }
      }
      setAutoFilled((prev) => ({ ...prev, ...af }));
      setResult(null);
      const parts = [];
      if (d.quota_m != null) parts.push(`quota ${d.quota_m} m`);
      if (d.building) parts.push("edificio da OSM");
      if (d.terrain_category) parts.push(`categoria ${d.terrain_category}`);
      const extra = d.building ? "" : (indirizzo ? " (edificio non trovato in mappa)" : " — aggiungi l'indirizzo per rilevare l'edificio");
      toast.success("Dati rilevati" + (parts.length ? ": " + parts.join(", ") : "") + extra);
    } catch (e) { toast.error("Rilevamento non riuscito"); }
    finally { setGeoLoading(false); }
  };

  const buildPayload = () => ({
    disposizione_falde: falde
      .map((f, i) => ({ ...f, falda_n: faldaNum(f, i) }))
      .filter((f) => !faldeSel || faldeSel.has(f.falda_n)),
    module_listino_id: moduleId,
    provincia: provincia || null,
    quota_m: quota ? Number(quota) : 0,
    altezza_edificio_m: altEd ? Number(altEd) : null,
    categoria_terreno: categoria,
    friction_preset: friction,
    contatto_tetto: sistema === "complanare" ? "zavorra" : contatto,
    building: {
      length_m: 0, width_m: 0, height_m: altEd ? Number(altEd) : 0,
      campo_aperto: campoAperto,
      dist_sup_m: dSup ? Number(dSup) : 0,
      dist_inf_m: dInf ? Number(dInf) : 0,
      dist_sx_m: dSx ? Number(dSx) : 0,
      dist_dx_m: dDx ? Number(dDx) : 0,
    },
    parapetto: {
      presente: hasParapet,
      diverso_per_lato: hasParapet && parapetDiff,
      altezza_m: hasParapet && !parapetDiff && parapetH ? Number(parapetH) : 0,
      h_sup_m: hasParapet && parapetDiff && pSup ? Number(pSup) : 0,
      h_inf_m: hasParapet && parapetDiff && pInf ? Number(pInf) : 0,
      h_sx_m: hasParapet && parapetDiff && pSx ? Number(pSx) : 0,
      h_dx_m: hasParapet && parapetDiff && pDx ? Number(pDx) : 0,
    },
    mode: engineMode,
    sistema,
    tetto_inclinato: isInclinato,
    roof_type: roofType,
    prsg_model_id: null,
    py_direction: pyDir,
    usa_zavorra: usaZavorra,
    cliente: selected?.summary?.cliente || null,
    field_image_url: fieldImg || null,
    gradi_inclinazione: sistema === "complanare" ? "2" : (gradiAuto || "25"),
    meta: {
      cliente: selected?.summary?.cliente || null,
      installatore: instName || null,
      is_partner: !!instPartner,
      consulenza_date: selected?.created_at || null,
      consulenza_ref: selected?.id ? String(selected.id).slice(-6) : null,
      installer_id: instId || null,
      comune: selected?.summary?.comune || comune || null,
      indirizzo: selected?.summary?.indirizzo || indirizzo || null,
      potenza_kwp: selected?.summary?.potenza_kwp ?? null,
      satellite_url: fieldImg || null,
    },
  });

  const compute = async () => {
    if (!selected || !hasFalde) { toast.error("Seleziona una consulenza con layout falde"); return; }
    if (selFaldeCount === 0) { toast.error("Seleziona almeno una falda da calcolare"); return; }
    if (!moduleId) { toast.error("Scegli il pannello"); return; }
    setComputing(true); setActivePrsg(null);
    try {
      const r = await api.post("/prsg-zavorre/compute", buildPayload());
      setResult(r.data);
      if (r.data.state === "DATI INSUFFICIENTI") {
        toast.warning("Dati insufficienti: " + (r.data.missing || []).join(", "));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo zavorre");
    } finally { setComputing(false); }
  };

  const openConfiguratore = () => {
    if (!installerId) { toast.error("Seleziona prima un installatore"); return; }
    window.open(`/configuratore/${installerId}?tool=tetto${moduleId ? `&module=${moduleId}` : ""}`, "_blank", "noopener");
    toast.info("Configuratore aperto in una nuova scheda: misura il tetto e crea il layout. Al termine torna qui e premi «Aggiorna».");
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await api.post("/prsg-zavorre/compute/pdf", buildPayload(), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const _cli = (selected?.summary?.cliente || "").trim().replace(/[\s/]+/g, "_");
      const _ref = selected?.id ? String(selected.id).slice(-6) : "";
      const _name = ["Zavorre", _cli, _ref].filter(Boolean).join("_") || `Zavorre_${provincia || "na"}`;
      a.href = url; a.download = `${_name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella generazione del PDF");
    } finally { setPdfLoading(false); }
  };

  const saveConsulenza = async () => {
    if (!selected || !hasFalde || !moduleId) { toast.error("Completa il calcolo prima di salvare"); return; }
    setSavingCt(true);
    try {
      if (link) {
        await api.post("/strumenti/salva", buildPayload());
        toast.success("Consulenza tecnica salvata nel configuratore");
      } else {
        await api.post("/consulenze-tecniche/zavorre", { ...buildPayload(), installer_id: instId || null, config_id: selected?.id || null });
        toast.success("Consulenza tecnica archiviata (configurazione + PDF)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio della consulenza");
    } finally { setSavingCt(false); }
  };

  const maxN = useMemo(() => Math.max(1, ...((result?.prsg || []).map((p) => p.peso_kg || 0))), [result]);
  // raggruppa PRSG per falda -> fila
  const byFalda = useMemo(() => {
    const m = {};
    (result?.prsg || []).forEach((p) => {
      (m[p.falda] = m[p.falda] || {});
      (m[p.falda][p.fila] = m[p.falda][p.fila] || []).push(p);
    });
    return m;
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="zavorre-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-primary" /> Pre-dimensionamento zavorre PRSG {isInclinato ? "(tetto inclinato → complanare)" : "(tetto piano)"}</DialogTitle>
          <DialogDescription>Motore parametrico NTC 2018 · risultato di pre-dimensionamento, non sostituisce la verifica strutturale del tecnico.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {selected && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-input bg-muted/30 p-2" data-testid="zavorre-automode-row">
              <Switch id="zav-auto" checked={autoMode} onCheckedChange={setAutoMode} data-testid="zavorre-automode-toggle" />
              <label htmlFor="zav-auto" className="text-xs font-semibold">Compilazione automatica</label>
              <span className="text-[11px] text-muted-foreground">{autoMode ? "campi acquisiti bloccati — disattiva il flag per modificarli a mano" : "modifica manuale attiva: puoi editare tutti i campi"}</span>
            </div>
          )}
          {/* 1. pannello + consulenza */}
          <div className="rounded-lg border border-input p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">1 · Pannello e layout campo</span>
              <div className="flex flex-wrap items-center gap-1">
                {!adminAll && !link && <Button type="button" size="sm" variant="ghost" onClick={openConfiguratore} data-testid="zavorre-new-consulenza"><ExternalLink className="mr-1.5 h-4 w-4" /> Nuova consulenza</Button>}
                {!link && <Button type="button" size="sm" variant="ghost" onClick={loadConfigs} data-testid="zavorre-reload"><RefreshCw className={`mr-1.5 h-4 w-4 ${loadingCfg ? "animate-spin" : ""}`} /> Aggiorna</Button>}
              </div>
            </div>
            <Select value={moduleId} disabled={autoMode && !!moduleAutoInfo} onValueChange={(v) => { setModuleId(v); setResult(null); if (selected?.id) localStorage.setItem(`zav_mod_${selected.id}`, v); }}>
              <SelectTrigger data-testid="zavorre-modulo"><SelectValue placeholder="Scegli il modulo…" /></SelectTrigger>
              <SelectContent>{modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.codice}</SelectItem>)}</SelectContent>
            </Select>
            {moduleAutoInfo && <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="zavorre-modulo-auto">Pannello {moduleAutoInfo}{autoMode ? " · bloccato" : ""}</p>}
            {moduleId && (() => {
              const sm = modules.find((x) => x.id === moduleId);
              const pw = sm?.specifiche?.["Peso (kg)"] ?? sm?.specifiche?.["Peso"] ?? sm?.specifiche?.["peso"];
              return pw ? <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid="zavorre-modulo-peso">Peso pannello adottato: <b>{String(pw).replace(".", ",")} kg</b> (da listino)</p> : null;
            })()}
            <div className="mt-2 space-y-1.5" data-testid="zavorre-config-list">
            {adminAll && (
              <Input value={cfgQ} onChange={(e) => setCfgQ(e.target.value)} placeholder="Cerca per azienda, agente o comune…" className="h-8 text-xs" data-testid="admin-cfg-search" />
            )}

              {loadingCfg ? <div className="py-3 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
                : configs.length === 0 ? (
                  <div className="rounded-md border border-dashed border-input bg-muted/20 p-3 text-center" data-testid="zavorre-no-config">
                    <p className="text-sm text-muted-foreground">Nessuna consulenza tetto adatta{adminAll ? "" : " per questo cliente"}.</p>
                    {!adminAll && <>
                      <Button type="button" size="sm" className="mt-2" onClick={openConfiguratore} data-testid="zavorre-create-consulenza">
                        <ExternalLink className="mr-1.5 h-4 w-4" /> Crea consulenza (misura il tetto)
                      </Button>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">Apri il configuratore, crea il layout del campo, poi torna qui e premi «Aggiorna».</p>
                    </>}
                  </div>
                )
                : configs.filter((c) => !adminAll || cfgMatch(c, cfgQ)).map((c) => {
                  const sel = selected?.id === c.id;
                  const s = c.summary || {};
                  const rt = s.roof_type || c.roof_type;
                  const meta = [];
                  if (s.comune) meta.push(s.comune);
                  meta.push(`${s.n_pannelli ?? "—"} pann.`);
                  meta.push(`${s.potenza_kwp ?? "—"} kWp`);
                  if (s.superficie_mq) meta.push(`${s.superficie_mq} m²`);
                  return (
                    <button key={c.id} type="button" onClick={() => { setSelected(c); setResult(null); }} data-testid={`zavorre-config-${c.id}`}
                      className={`flex w-full items-center justify-between gap-2 rounded-md border p-2 text-left text-sm transition-colors ${sel ? "border-primary bg-primary/10" : "border-input hover:bg-accent"}`}>
                      <span className="min-w-0">
                        {adminAll && <AdminCfgLine c={c} />}
                        <span className="font-semibold">{s.cliente || "—"}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{meta.join(" · ")}</span>
                        {rt && <Badge variant="secondary" className="ml-2 text-[10px]">{rt === "piano" ? "tetto piano" : rt}</Badge>}
                      </span>
                    </button>
                  );
                })}
            </div>
            {selected && hasFalde && faldeLayout.length > 0 && (
              <div className="mt-3 rounded-md border border-input bg-muted/20 p-2" data-testid="zavorre-falde-select">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">Falde da calcolare</span>
                  <button type="button" className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                    data-testid="zavorre-falde-toggle-all"
                    onClick={() => { setResult(null); setFaldeSel(allFaldeSel ? new Set() : new Set(faldeLayout.map((x) => x.n))); }}>
                    {allFaldeSel ? "Deseleziona tutte" : "Seleziona tutte"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {faldeLayout.map(({ f, n }) => {
                    const checked = !faldeSel || faldeSel.has(n);
                    return (
                      <label key={n} htmlFor={`zav-falda-${n}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${checked ? "border-primary bg-primary/10" : "border-input hover:bg-accent"}`}
                        data-testid={`zavorre-falda-option-${n}`}>
                        <Checkbox id={`zav-falda-${n}`} checked={checked} onCheckedChange={() => toggleFalda(n)} data-testid={`zavorre-falda-checkbox-${n}`} />
                        <span className="font-semibold">Falda {n}</span>
                        <span className="text-muted-foreground">{faldaPanelCount(f)} pann.</span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[10px] text-muted-foreground">{selFaldeCount} di {faldeLayout.length} falde selezionate · i numeri originali vengono mantenuti nel calcolo e nel PDF</p>
              </div>
            )}
            {selected && isInclinato && (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300" data-testid="zavorre-inclinato-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Consulenza a <b>tetto inclinato</b>: il calcolo viene eseguito in <b>modalità complanare</b> (pannello su zavorra ~2°). L'inclinazione reale delle falde non è considerata — verifica l'applicabilità.</span>
              </div>
            )}
            {selected && !hasFalde && <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">Questa consulenza non ha ancora il layout falde.</p>}
          </div>

          {/* 2. località + edificio */}
          <div className="rounded-lg border border-input p-3">
            <span className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-widest text-primary">2 · Località ed edificio</span>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <div>
                <Label className="text-xs">Provincia</Label>
                <Select value={provincia} disabled={autoMode && autoFilled.provincia} onValueChange={(v) => { setProvincia(v); setResult(null); }}>
                  <SelectTrigger data-testid="zavorre-provincia"><SelectValue placeholder="Provincia…" /></SelectTrigger>
                  <SelectContent className="max-h-64">{province.map((p) => <SelectItem key={p.sigla} value={p.sigla}>{p.nome} ({p.sigla})</SelectItem>)}</SelectContent>
                </Select>
                {provInfo && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="zavorre-zona-hint">Zona vento <b>{provInfo.zona}</b> · {provInfo.zona_label}</p>}
              </div>
              <div>
                <Label className="text-xs">Categoria esposizione</Label>
                <Select value={categoria} onValueChange={(v) => { setCategoria(v); setCatInfo(""); setResult(null); }}>
                  <SelectTrigger data-testid="zavorre-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{TERR.map((t) => <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {catInfo && <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="zavorre-categoria-auto">{catInfo}</p>}
              </div>
              <div>
                <Label className="text-xs">Attrito copertura</Label>
                <Select value={friction} onValueChange={(v) => { setFriction(v); setResult(null); }}>
                  <SelectTrigger data-testid="zavorre-friction"><SelectValue /></SelectTrigger>
                  <SelectContent>{frictionOpts.map((t) => <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">A contatto col tetto</Label>
                <Select value={contatto} onValueChange={(v) => { setContatto(v); setResult(null); }}>
                  <SelectTrigger data-testid="zavorre-contatto"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zavorra">Zavorra (blocchi sul manto)</SelectItem>
                    <SelectItem value="staffa">Staffa PRSG (presagomata sul manto)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-0.5 text-[10px] text-muted-foreground">determina il μ usato nella verifica allo scorrimento (interfaccia unica)</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Comune</Label>
                <Input value={comune} disabled={autoMode && autoFilled.comune} data-testid="zavorre-comune" onChange={(e) => { setComune(e.target.value); setResult(null); }} placeholder="es. Manerbio" className="h-8" />
              </div>
              <div>
                <Label className="text-xs">Indirizzo (per rilevare l'edificio)</Label>
                <Input value={indirizzo} disabled={autoMode && autoFilled.indirizzo} data-testid="zavorre-indirizzo" onChange={(e) => { setIndirizzo(e.target.value); setResult(null); }} placeholder="es. Via Cremona 123" className="h-8" />
              </div>
              <div><Label className="text-xs">Quota s.l.m. (m)</Label><Input type="number" value={quota} disabled={autoMode && autoFilled.quota} data-testid="zavorre-quota" onChange={(e) => { setQuota(e.target.value); setResult(null); }} className="h-8" /></div>
              <div><Label className="text-xs">Altezza edificio (m) *</Label><Input type="number" value={altEd} disabled={autoMode && autoFilled.altEd} data-testid="zavorre-altezza" onChange={(e) => { setAltEd(e.target.value); setResult(null); }} className="h-8" /></div>
              <div className="md:col-span-2 rounded-md border border-input bg-muted/20 p-2" data-testid="zavorre-bordi-row">
                <div className="flex items-center gap-2">
                  <Switch id="zav-campo-aperto" checked={campoAperto} onCheckedChange={(v) => { setCampoAperto(v); setResult(null); }} data-testid="zavorre-campo-aperto-toggle" />
                  <label htmlFor="zav-campo-aperto" className="text-xs font-semibold cursor-pointer">Campo aperto / installazione a terra</label>
                  <span className="text-[10px] text-muted-foreground">niente bordi edificio: file esterne trattate come perimetrali (conservativo)</span>
                </div>
                {!campoAperto && (
                  <div className="mt-2">
                    <Label className="text-[11px] font-semibold">Distanza del campo dai bordi del tetto (m)</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div><Label className="text-[10px]">Superiore</Label><Input type="number" step="0.01" value={dSup} data-testid="zavorre-dist-sup" onChange={(e) => { setDSup(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Inferiore</Label><Input type="number" step="0.01" value={dInf} data-testid="zavorre-dist-inf" onChange={(e) => { setDInf(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Sinistra</Label><Input type="number" step="0.01" value={dSx} data-testid="zavorre-dist-sx" onChange={(e) => { setDSx(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Destra</Label><Input type="number" step="0.01" value={dDx} data-testid="zavorre-dist-dx" onChange={(e) => { setDDx(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                    </div>
                    <p className="mt-1 text-[10px] text-muted-foreground">0 = campo a filo bordo. Da queste distanze e dall'ingombro del campo il sistema ricava le dimensioni edificio e la fascia di bordo NTC (e/10) per ogni lato.</p>
                  </div>
                )}
              </div>
              <div className="md:col-span-2 rounded-md border border-input bg-muted/20 p-2" data-testid="zavorre-parapetto-row">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch id="zav-parapet" checked={hasParapet} onCheckedChange={(v) => { setHasParapet(v); if (!v) { setParapetH(""); setParapetDiff(false); } setResult(null); }} data-testid="zavorre-parapetto-toggle" />
                    <label htmlFor="zav-parapet" className="text-xs font-semibold cursor-pointer">Parapetto / protezione</label>
                  </div>
                  {hasParapet && (
                    <div className="flex items-center gap-2">
                      <Switch id="zav-parapet-diff" checked={parapetDiff} onCheckedChange={(v) => { setParapetDiff(v); setResult(null); }} data-testid="zavorre-parapetto-diff-toggle" />
                      <label htmlFor="zav-parapet-diff" className="text-xs cursor-pointer">Diverso sui 4 lati?</label>
                    </div>
                  )}
                  <span className="text-[10px] text-muted-foreground">riduce i coefficienti su bordi/angoli (da calibrare)</span>
                </div>
                {hasParapet && !parapetDiff && (
                  <div className="mt-2 flex items-center gap-2">
                    <Label className="text-xs">Altezza parapetto (m)</Label>
                    <Input type="number" step="0.01" value={parapetH} data-testid="zavorre-parapetto-altezza" onChange={(e) => { setParapetH(e.target.value); setResult(null); }} className="h-8 w-28" placeholder="es. 0,50" />
                    <span className="text-[10px] text-muted-foreground">stesso parapetto sui 4 lati</span>
                  </div>
                )}
                {hasParapet && parapetDiff && (
                  <div className="mt-2">
                    <Label className="text-[11px] font-semibold">Altezza parapetto per lato (m) — 0 = nessun parapetto su quel lato</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div><Label className="text-[10px]">Superiore</Label><Input type="number" step="0.01" value={pSup} data-testid="zavorre-parapetto-sup" onChange={(e) => { setPSup(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Inferiore</Label><Input type="number" step="0.01" value={pInf} data-testid="zavorre-parapetto-inf" onChange={(e) => { setPInf(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Sinistra</Label><Input type="number" step="0.01" value={pSx} data-testid="zavorre-parapetto-sx" onChange={(e) => { setPSx(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                      <div><Label className="text-[10px]">Destra</Label><Input type="number" step="0.01" value={pDx} data-testid="zavorre-parapetto-dx" onChange={(e) => { setPDx(e.target.value); setResult(null); }} className="h-8" placeholder="0" /></div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Sistema di montaggio</Label>
                <Select value={sistema} disabled={isInclinato} onValueChange={(v) => { setSistema(v); if (v === "complanare") setContatto("zavorra"); setResult(null); }}>
                  <SelectTrigger data-testid="zavorre-sistema"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prsg">PRSG inclinato (presagomate)</SelectItem>
                    <SelectItem value="complanare">Complanare (pannello su zavorra)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-0.5 text-[10px] text-muted-foreground">{isInclinato ? "Tetto inclinato: calcolato in modalità complanare (pannello su zavorra); l'inclinazione reale non è considerata." : (sistema === "complanare" ? "tetto piano, pannello ~2° parallelo al piano: niente PRSG, peso = zavorre + pannello" : "pannelli su staffe presagomate inclinate")}</p>
              </div>
              <div>
                <Label className="text-xs">Modello PRSG</Label>
                <div className="flex h-8 items-center rounded-md border border-input bg-muted/40 px-2 text-xs" data-testid="zavorre-prsg-model">
                  {sistema === "complanare" ? "Complanare 2° · pannello su zavorra (no PRSG)" : (gradiAuto ? `PRSG ${gradiAuto}° · acquisito dalla consulenza` : "PRSG 25° · inclinazione non ricevuta (default)")}
                </div>
                {sistema !== "complanare" && !gradiAuto && <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">Inclinazione staffa non ricevuta dalla consulenza: usato 25° di default, verifica.</p>}
              </div>
              <div>
                <Label className="text-xs">Motore di calcolo</Label>
                <div className="flex h-8 items-center rounded-md border border-input bg-muted/40 px-2 text-xs text-muted-foreground" data-testid="zavorre-engine-mode">Aerodinamico PRSG (pre-dim.)</div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={rileva} disabled={geoLoading} data-testid="zavorre-rileva">
                {geoLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MapPin className="mr-1.5 h-4 w-4" />} Rileva dati (quota / edificio)
              </Button>
              <span className="text-[11px] text-muted-foreground">Comune → quota s.l.m.; con l'indirizzo stima anche altezza e dimensioni edificio da mappa.</span>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">* Dato indispensabile. Le distanze del campo dai bordi del tetto definiscono la zonizzazione NTC (fasce di bordo) per ciascun lato.</p>
          </div>

          {/* prodotti zavorra Torri */}
          <div className="flex items-start gap-3 rounded-lg border border-input bg-muted/30 p-3" data-testid="zavorre-usa-row">
            <Switch id="zav-usa" checked={usaZavorra} onCheckedChange={(v) => { setUsaZavorra(v); setResult(null); }} data-testid="zavorre-usa-toggle" />
            <label htmlFor="zav-usa" className="cursor-pointer text-sm font-semibold leading-tight">
              Includi prodotti zavorra Torri
              <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                {usaZavorra
                  ? "il PDF propone i blocchi zavorra Torri (arrotondamento commerciale) e li inserisce nella distinta materiali"
                  : "prodotti zavorra esclusi: viene indicato solo il peso teorico da applicare (zavorre a cura dell'installatore). Il calcolo strutturale resta invariato."}
              </span>
            </label>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={compute} disabled={!selected || !hasFalde || !moduleId || computing} data-testid="zavorre-compute">
              {computing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Calculator className="mr-1.5 h-4 w-4" />} Calcola zavorre
            </Button>
          </div>

          {/* RISULTATO */}
          {result && (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="zavorre-result">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge className={STATE_STYLE[result.state] || ""} data-testid="zavorre-state">{result.state}</Badge>
                <span className="text-[11px] text-muted-foreground">{result.version}</span>
                {result.engine_mode && <Badge variant="outline" className="text-[10px]" data-testid="zavorre-mode">{result.engine_mode}</Badge>}
                {result.prsg_model?.label && <Badge variant="outline" className="text-[10px]">{result.prsg_model.label} · {result.prsg_model.mass_kg} kg</Badge>}
                {!result.aero_validated && <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid="zavorre-davalidare">coefficienti aerodinamici da validare</span>}
                {!usaZavorra && <Badge variant="outline" className="text-[10px]" data-testid="zavorre-usa-badge">zavorre a cura installatore</Badge>}
                {result.state !== "DATI INSUFFICIENTI" && (
                  <div className="ml-auto flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={downloadPdf} disabled={pdfLoading} data-testid="zavorre-pdf">
                      {pdfLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Scarica PDF
                    </Button>
                    <Button type="button" size="sm" onClick={saveConsulenza} disabled={savingCt} data-testid="zavorre-save-ct">
                      {savingCt ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />} Salva consulenza
                    </Button>
                  </div>
                )}
              </div>

              {result.state === "DATI INSUFFICIENTI" ? (
                <div className="flex items-start gap-1.5 rounded-md border border-slate-400/40 bg-slate-400/10 p-2 text-xs" data-testid="zavorre-missing">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" /> Dati mancanti: <b>{(result.missing || []).join(", ")}</b>
                </div>
              ) : (
                <>
                  {result.warnings?.length > 0 && (
                    <div className="mb-2 space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">
                      {result.warnings.map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</div>)}
                    </div>
                  )}
                  {/* riepilogo vento */}
                  <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
                    <Badge variant="outline" data-testid="zavorre-qdesign">q_design {result.wind?.q_design} N/m²</Badge>
                    <Badge variant="outline">v_b {result.wind?.vb} m/s</Badge>
                    <Badge variant="outline">c_e {result.wind?.ce}</Badge>
                    <Badge variant="outline">zona {result.wind?.zona}</Badge>
                  </div>

                  {/* layout: riproduce la matrice reale del configuratore (vuoti/sfalsamenti/allineamenti) */}
                  <div className="mb-3 space-y-3" data-testid="zavorre-layout">
                    {(result?.campo_matrici || []).map((cm) => {
                      const fa = cm.falda;
                      const matrix = cm.matrix || [];
                      const width = cm.width || Math.max(1, ...matrix.map((r) => r.length));
                      const staffeByRow = byFalda[fa] || {};
                      const CELL = 44;
                      return (
                        <div key={fa} className="overflow-x-auto rounded-md border border-input p-2" style={roofStyle} data-testid={`zavorre-falda-${fa}`}>
                          <div className="mb-1 inline-block rounded bg-background/80 px-1.5 text-[11px] font-bold text-foreground">Falda {fa}</div>
                          <div className="w-fit pt-3" style={{ width: (width + 1) * CELL }}>
                            {matrix.map((row, ri) => {
                              const byCol = {};
                              (staffeByRow[ri + 1] || []).forEach((p) => { byCol[p.col] = p; });
                              return (
                                <div key={ri} className="relative flex items-end" style={{ height: 60 }}>
                                  {row.map((v, ci) => (
                                    <div key={ci} style={{ width: CELL, height: 48 }}
                                      className={v ? "self-end rounded-sm border border-slate-400/60 bg-background/40" : "bg-transparent"} title={v ? "Modulo" : "Cella vuota"} />
                                  ))}
                                  {Array.from({ length: width + 1 }).map((_, bc) => {
                                    const p = byCol[bc];
                                    if (!p) return null;
                                    const c = p.non_verificato ? "#dc2626" : weightColor(p.peso_kg || 0, maxN);
                                    return (
                                      <button key={`s${bc}`} type="button" onClick={() => setActivePrsg(p)} data-testid={`zavorre-prsg-${p.n}`}
                                        title={`Fila ${ri + 1} · PRSG ${p.n} · ${p.zona_campo}${p.non_verificato ? " · NON VERIF." : ` · ${p.peso_kg}kg`}`}
                                        className="absolute bottom-0 z-10 flex -translate-x-1/2 flex-col items-center transition-transform hover:scale-110"
                                        style={{ left: bc * CELL }}>
                                        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white shadow ${activePrsg?.n === p.n ? "ring-2 ring-offset-1 ring-primary" : ""}`}
                                          style={{ backgroundColor: c }}>{p.n}</span>
                                        <span className="mt-0.5 w-[3px] rounded" style={{ height: 46, backgroundColor: c }} />
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: weightColor(1, maxN) }} /> peso minore</span>
                      <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: weightColor(maxN, maxN) }} /> peso maggiore</span>
                      <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full bg-red-600" /> non verificato</span>
                      <span>· il badge numerato è la staffa PRSG (colore = peso da applicare) · clic per il dettaglio</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground" data-testid="zavorre-bg-note">
                      {"Lo schema riproduce la matrice reale del configuratore: celle vuote, sfalsamenti e allineamenti sono conservati. Le linee verticali sono le staffe PRSG; i rettangoli sono i moduli. La vista satellitare è un elemento separato, qui sotto."}
                    </p>
                  </div>

                  {fieldImg && (
                    <div className="mb-3 rounded-md border border-input p-2" data-testid="zavorre-satellite">
                      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Vista satellitare (configuratore)</div>
                      <img src={fieldImg} alt="Vista campo satellitare" className="max-h-72 w-full rounded object-contain" />
                      <p className="mt-1 text-[10px] text-muted-foreground">Elemento distinto dallo schema pesi: rappresenta la vista reale del campo dal configuratore.</p>
                    </div>
                  )}

                  {/* dettaglio PRSG selezionata */}
                  {activePrsg && (
                    <div className="mb-3 rounded-md border border-primary/50 bg-background p-3 text-xs" data-testid="zavorre-prsg-detail">
                      <div className="mb-1 font-bold uppercase tracking-wide text-primary">Zavorramento PRSG {activePrsg.n}</div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        <div>Zona campo: <b className="capitalize">{activePrsg.zona_campo}</b> · fila <b>{activePrsg.pos_fila}</b></div>
                        <div>Area tributaria: <b>{activePrsg.area_tributaria_m2} m²</b></div>
                        <div>Peso da applicare: <b>{activePrsg.non_verificato ? "—" : `${activePrsg.peso_kg} kg`}</b></div>
                        <div>Verifica governante: <b className="capitalize">{activePrsg.governante}</b></div>
                        <div>Sollevamento richiesto: <b>{activePrsg.req_sollevamento_kg ?? "—"} kg</b></div>
                        <div>Scorrimento richiesto: <b>{activePrsg.req_scorrimento_kg ?? "non verif."} kg</b></div>
                        <div>Ribaltamento richiesto: <b>{activePrsg.req_ribaltamento_kg ?? "—"} kg</b></div>
                        <div>Direzione vento critica: <b>{activePrsg.direzione_critica}°</b></div>
                        <div>F_lift {activePrsg.F_lift_N} N · F_drag {activePrsg.F_drag_N} N</div>
                      </div>
                    </div>
                  )}

                  {/* analisi pesi + carico */}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded-md border border-input p-2 text-xs" data-testid="zavorre-analisi">
                      <div className="mb-1 font-bold uppercase tracking-wide text-muted-foreground">Analisi dei pesi da applicare</div>
                      <div>Peso totale: <b>{result.analisi_pesi?.peso_totale_kg} kg</b></div>
                      <div>Peso medio per PRSG: <b>{result.analisi_pesi?.peso_medio_prsg_kg} kg</b></div>
                      <div>Peso min / max per PRSG: <b>{result.analisi_pesi?.peso_min_prsg_kg}</b> / <b>{result.analisi_pesi?.peso_max_prsg_kg} kg</b></div>
                      <div>PRSG totali: <b>{result.n_prsg}</b> · pannelli <b>{result.n_pannelli}</b></div>
                    </div>
                    <div className="rounded-md border border-input p-2 text-xs" data-testid="zavorre-carico">
                      <div className="mb-1 font-bold uppercase tracking-wide text-muted-foreground">Carico sulla copertura</div>
                      <div>Pannelli: {result.carico_copertura?.peso_pannelli_kg} kg · PRSG: {result.carico_copertura?.peso_prsg_kg} kg</div>
                      {result.inputs?.module?.weight_kg ? <div data-testid="zavorre-carico-peso-pannello">Peso pannello adottato: <b>{String(result.inputs.module.weight_kg).replace(".", ",")} kg</b> (da listino)</div> : null}
                      <div>Zavorramento: {result.carico_copertura?.peso_zavorre_kg} kg · Totale: <b>{result.carico_copertura?.peso_sistema_kg} kg</b></div>
                      <div className="mt-1 text-[13px]">Carico permanente medio: <b>{result.carico_copertura?.kg_m2_medio ?? "—"} kg/m²</b></div>
                      <div className="mt-1 flex items-start gap-1 text-[10px] text-muted-foreground"><ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" />{result.carico_copertura?.nota}</div>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">Risultato di PRE-DIMENSIONAMENTO: non sostituisce la verifica strutturale del tecnico.</p>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
